import type { QuoteStatus, QuoteDisplayStatus, QuoteType } from './index'

// ─── Labels & badges ──────────────────────────────────────────────────────────

// Ronde 4 W3 (spec 03 §8.5) — `GEREED` en `OMGEZET` toegevoegd; `DEFINITIEF`
// blijft als legacy-label zichtbaar voor records die nog niet zijn gemigreerd.
export const QUOTE_STATUS_LABELS: Record<QuoteDisplayStatus, string> = {
  CONCEPT:      'Concept',
  GEREED:       'Gereed voor controle',
  DEFINITIEF:   'Definitief',
  VERSTUURD:    'Verstuurd',
  GEACCEPTEERD: 'Geaccepteerd',
  AFGEWEZEN:    'Afgewezen',
  VERLOPEN:     'Verlopen',
  OMGEZET:      'Omgezet',
}

export const QUOTE_STATUS_BADGE: Record<
  QuoteDisplayStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  CONCEPT:      'secondary',
  GEREED:       'info',
  DEFINITIEF:   'info',
  VERSTUURD:    'info',
  GEACCEPTEERD: 'success',
  AFGEWEZEN:    'danger',
  VERLOPEN:     'warning',
  OMGEZET:      'success',
}

export const QUOTE_STATUSES: readonly QuoteStatus[] = [
  'CONCEPT',
  'GEREED',
  'DEFINITIEF',
  'VERSTUURD',
  'GEACCEPTEERD',
  'AFGEWEZEN',
  'OMGEZET',
] as const

export const QUOTE_DISPLAY_STATUSES: readonly QuoteDisplayStatus[] = [
  'CONCEPT',
  'GEREED',
  'DEFINITIEF',
  'VERSTUURD',
  'GEACCEPTEERD',
  'AFGEWEZEN',
  'VERLOPEN',
  'OMGEZET',
] as const

export const QUOTE_TYPE_LABELS: Record<QuoteType, string> = {
  dienstverlening: 'Service',
  project:         'Project',
  onderhoud:       'Onderhoud',
  combinatie:      'Combinatie',
}

export function deriveQuoteType(flags: {
  serviceEnabled: boolean
  projectEnabled: boolean
  maintenanceEnabled: boolean
}): QuoteType {
  const active = [flags.serviceEnabled, flags.projectEnabled, flags.maintenanceEnabled].filter(Boolean).length
  if (active > 1) return 'combinatie'
  if (flags.projectEnabled) return 'project'
  if (flags.maintenanceEnabled) return 'onderhoud'
  return 'dienstverlening'
}

// ─── List & detail DTOs ───────────────────────────────────────────────────────

export interface QuoteListRowDTO {
  id: string
  quoteNumber: string
  entityId: string
  // Ronde 4 W3 — clientId is nullable voor prospect-offertes.
  clientId: string | null
  projectName: string
  status: QuoteStatus
  displayStatus: QuoteDisplayStatus
  quoteDate: string
  validUntil: string
  subtotal: number
  serviceEnabled: boolean
  projectEnabled: boolean
  maintenanceEnabled: boolean
  // Ronde 4 W3 — travel-flag voor lijst-overzicht.
  travelEnabled: boolean
  createdAt: string
  entity: { id: string; name: string } | null
  client: { id: string; name: string; clientNumber: string | null } | null
  // Ronde 4 W3 — toon prospect-naam in lijst wanneer client = null.
  prospectCompanyName: string | null
  // Spec 20 §2.4.3 — marge-percentage voor de lijst-badge. Alleen gevuld voor
  // ADMIN/MANAGER (server-side rol-gated); `null` voor andere rollen of wanneer
  // er geen verkoopprijs is.
  marginPercentage: number | null
}

export interface QuoteListMeta {
  total: number
  page: number
  limit: number
  kpi: {
    totalQuotes: number
    conceptCount: number
    openValue: number
    acceptedValue: number
  }
}

export interface QuoteMaterialDTO {
  id: string
  position: number
  description: string
  quantity: number
  purchasePrice: number
  marginPercentage: number
}

export interface QuoteAuditEntryDTO {
  id: string
  action: string
  fromStatus: QuoteStatus | null
  toStatus: QuoteStatus | null
  note: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  performedBy: { id: string; name: string | null; email: string | null }
}

// Ronde 4 W6 — Order-model is verwijderd. QuoteRelatedOrderDTO bestaat niet
// meer; de offerte → werkbon-conversie (Ronde 4 W3 §8.4) levert direct een
// werkbon op.

export interface QuoteDetailDTO {
  id: string
  quoteNumber: string
  status: QuoteStatus
  displayStatus: QuoteDisplayStatus
  version: number
  projectName: string
  quoteDate: string
  validUntil: string
  remarks: string | null
  serviceEnabled: boolean
  serviceInput: Record<string, unknown> | null
  projectEnabled: boolean
  projectInput: Record<string, unknown> | null
  maintenanceEnabled: boolean
  maintenanceInput: Record<string, unknown> | null
  // Ronde 4 W3 (spec 03 §4) — aparte travel-sectie.
  travelEnabled: boolean
  travelInput: Record<string, unknown> | null
  travelTotal: number
  serviceTotal: number
  projectTotal: number
  maintenanceTotal: number
  subtotal: number
  vatAmount: number
  totalIncVat: number
  // Ronde 4 W3 (spec 03 §5) — per-offerte tarief-overrides.
  tariffOverrides: Record<string, number> | null
  pdfPath: string | null
  sentAt: string | null
  decidedAt: string | null
  createdAt: string
  updatedAt: string
  entity: { id: string; name: string; quotePrefix: string | null } | null
  client: {
    id: string
    clientNumber: string | null
    name: string
    vatNumber: string | null
    kvkNumber: string | null
    street: string | null
    houseNumber: string | null
    postalCode: string | null
    city: string | null
    country: string | null
  } | null
  // Ronde 4 W3 (spec 03 §2.2) — prospect-gegevens; gevuld wanneer client null is.
  prospect: {
    companyName: string | null
    contactName: string | null
    email: string | null
    phone: string | null
    street: string | null
    houseNumber: string | null
    postalCode: string | null
    city: string | null
    country: string | null
    vatNumber: string | null
    kvkNumber: string | null
  } | null
  convertedToClientId: string | null
  clientContact: {
    id: string
    firstName: string
    lastName: string
    functionTitle: string | null
    email: string | null
  } | null
  materials: QuoteMaterialDTO[]
  audits: QuoteAuditEntryDTO[]
  createdBy: { id: string; name: string | null; email: string } | null
  // Spec 20 §2.4.2 — marge-analyse (kostprijs vs. verkoopprijs). Alleen gevuld
  // voor ADMIN/MANAGER (server-side rol-gated); `null` voor andere rollen zodat
  // er geen gevoelige financiële data lekt.
  margin: QuoteMarginDTO | null
}

/// Spec 20 §2.4 — marge-blok op de offerte-detail (overzicht-tab).
export interface QuoteMarginDTO {
  sellPrice: number // verkoopprijs excl. BTW
  costPrice: number // interne kosten
  margin: number // sellPrice − costPrice
  marginPercentage: number // (margin / sellPrice) × 100
}

// ─── Create-context (klanten + entiteiten + tarieven) ─────────────────────────

export interface QuoteCreateClient {
  id: string
  name: string
  clientNumber: string | null
}

export interface QuoteCreateEntity {
  id: string
  name: string
}

export interface QuoteCreateContext {
  clients: QuoteCreateClient[]
  entities: QuoteCreateEntity[]
  tariffs: import('./index').TariffMap
  // Spec 20 §2.3.2 — globale risico-opslag-default (% over projecttotaal) voor
  // de project-calculator. Per-offerte overschrijfbaar via
  // `ProjectInput.riskSurchargeOverride`.
  riskDefaultPct: number
}

// ─── Defaults voor de calculatie-editor ──────────────────────────────────────

export const DEFAULT_SERVICE_INPUT: import('./index').ServiceInput = {
  packageKey: 'basis',
  numberOfMonteurs: 2,
  hoursPerMonteur: 8,
  shiftType: 'geen',
  kmPerDay: 0,
  numberOfDays: 1,
  travelTimePerDay: 0,
}

export const DEFAULT_PROJECT_ROLES: import('./index').ProjectRoleInput[] = [
  { roleKey: 'projectmanager',    hours: 0 },
  { roleKey: 'software_engineer', hours: 0 },
  { roleKey: 'projectmonteur',    hours: 0 },
]

export const DEFAULT_MAINTENANCE_INPUT: import('./index').MaintenanceInput = {
  normalHours: 0,
  overtime02Hours: 0,
  overtime24Hours: 0,
  saturdayHours: 0,
  sundayHours: 0,
}

// Ronde 4 W3 (spec 03 §4) — default voor de aparte travel-sectie. UI laat
// de gebruiker dit aanvinken; bij uitgeschakelde sectie wordt travelTotal = 0.
export const DEFAULT_TRAVEL_INPUT: import('./index').TravelInput = {
  kmPerDay: 0,
  numberOfDays: 1,
  travelTimePerDay: 0,
}

// Ronde 4 W3 (spec 03 §2.8) — drie modi in de create-modal.
export type QuoteClientMode = 'BESTAAND' | 'NIEUW' | 'PROSPECT'

// Ronde 4 W3 — structured validation-issue zoals teruggegeven door API
// wanneer create/update Zod-validatie faalt. Frontend mapt dit per veld.
export interface QuoteValidationIssue {
  field: string
  message: string
  code?: string
}
